/**
 * @fileoverview パスワードリセット初期画面
 *
 * このファイルでは、パスワードを忘れたユーザーがリセットプロセスを開始するための
 * 初期画面を提供します。メールアドレス入力フォームとリセットリクエストの
 * 送信機能を実装しています。
 */

/**
 * パスワードリセット初期画面コンポーネント
 *
 * ユーザーがメールアドレスを入力してパスワードリセットリクエストを
 * 送信するためのフォームを提供します。入力値の検証と
 * パスワードリセットメール送信APIとの連携を行います。
 *
 * @returns {JSX.Element} パスワードリセット初期フォーム
 */

import { useEffect, useState } from "react";
import { Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation } from "@tanstack/react-query";

import { useNavigate } from "@/router";
import { Card } from "@/components/card";
import { Button } from "@/components/button";
import { sendPasswordResetMail } from "@/api/sendPasswordResetMail";

export default function ResetPasswordPage() {
  const form = useForm({
    mode: "uncontrolled",
    initialValues: { email: "" },
  });

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messageError = (message: string) => {
    setErrorMessage(message);
  };

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationKey: ["sendPasswordResetMail"],
    mutationFn: async (email: string) => await sendPasswordResetMail(email),
  });

  useEffect(() => {
    if (isSuccess) {
      navigate("/reset-password/confirm-email");
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      messageError(
        "エラーが発生しました。10分程度時間をおいてから再度パスワードリセットをお試しください。それでもエラーが発生する場合はサポートにお問い合わせください。"
      );
    }
  }, [isError]);

  const handleSendMailToResetPassword = (values: typeof form.values) => {
    mutate(values.email);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Card
      title="パスワードリセット"
      description="
          パスワードリセットのため、登録しているメールアドレスを入力してください。
          登録しているメールアドレスにパスワードリセット用のURLを送ります。"
    >
      <form onSubmit={form.onSubmit(handleSendMailToResetPassword)}>
        <Stack className="gap-10">
          {errorMessage && (
            <Text c="red" fw={700}>
              {errorMessage}
            </Text>
          )}
          <div className="flex flex-col gap-5">
            <TextInput
              key={form.key("email")}
              {...form.getInputProps("email")}
              label="メールアドレス"
              placeholder="email"
              required={true}
            />
          </div>
          <div className="flex justify-between">
            <Button text="戻る" onClick={handleBack} disabled={isPending} />
            <Button
              text="送信"
              type="submit"
              disabled={isPending}
              loading={isPending}
            />
          </div>
        </Stack>
      </form>
    </Card>
  );
}
